import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Edit2, Trash2, MapPin, Save, X, GripVertical, Navigation, CircleDot } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';

const API = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const mapContainerStyle = {
  width: '100%',
  height: '400px'
};

const defaultCenter = {
  lat: 32.0853,
  lng: 34.7818
};

const AdminTestSettingsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [testMaps, setTestMaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingMap, setEditingMap] = useState(null);
  const [formData, setFormData] = useState({ name: '', locations: [] });
  const [saving, setSaving] = useState(false);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  
  // Walk & Drop mode
  const [walkMode, setWalkMode] = useState(false);
  const [liveLocation, setLiveLocation] = useState(null);
  const [gpsWatchId, setGpsWatchId] = useState(null);
  const mapRef = React.useRef(null);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY
  });

  useEffect(() => {
    fetchTestMaps();
    // Get user's current location for map center
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setMapCenter({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        () => {} // Ignore errors, use default center
      );
    }
  }, []);

  const fetchTestMaps = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/test-maps`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestMaps(response.data);
    } catch (error) {
      toast.error('Failed to load test maps');
    } finally {
      setLoading(false);
    }
  };

  const handleMapClick = useCallback((e) => {
    const newLocation = {
      order: formData.locations.length + 1,
      name: `Test Location ${formData.locations.length + 1}`,
      lat: e.latLng.lat(),
      lng: e.latLng.lng()
    };
    setFormData(prev => ({
      ...prev,
      locations: [...prev.locations, newLocation]
    }));
  }, [formData.locations.length]);

  const handleMarkerDrag = useCallback((index, e) => {
    const updatedLocations = [...formData.locations];
    updatedLocations[index] = {
      ...updatedLocations[index],
      lat: e.latLng.lat(),
      lng: e.latLng.lng()
    };
    setFormData(prev => ({ ...prev, locations: updatedLocations }));
  }, [formData.locations]);

  const handleRemoveLocation = (index) => {
    const updatedLocations = formData.locations
      .filter((_, i) => i !== index)
      .map((loc, i) => ({ ...loc, order: i + 1 }));
    setFormData(prev => ({ ...prev, locations: updatedLocations }));
  };

  const handleLocationNameChange = (index, name) => {
    const updatedLocations = [...formData.locations];
    updatedLocations[index] = { ...updatedLocations[index], name };
    setFormData(prev => ({ ...prev, locations: updatedLocations }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a name for the test map');
      return;
    }
    if (formData.locations.length === 0) {
      toast.error('Please add at least one test location');
      return;
    }

    setSaving(true);
    try {
      stopWalkMode();
      if (editingMap) {
        await axios.put(`${API}/api/admin/test-maps/${editingMap.map_id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Test map updated');
      } else {
        await axios.post(`${API}/api/admin/test-maps`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Test map created');
      }
      setShowForm(false);
      setEditingMap(null);
      setFormData({ name: '', locations: [] });
      fetchTestMaps();
    } catch (error) {
      toast.error('Failed to save test map');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (testMap) => {
    setEditingMap(testMap);
    setFormData({
      name: testMap.name,
      locations: testMap.locations || []
    });
    if (testMap.locations && testMap.locations.length > 0) {
      setMapCenter({
        lat: testMap.locations[0].lat,
        lng: testMap.locations[0].lng
      });
    }
    setShowForm(true);
  };

  const handleDelete = async (mapId) => {
    if (!window.confirm('Are you sure you want to delete this test map?')) return;
    
    try {
      await axios.delete(`${API}/api/admin/test-maps/${mapId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Test map deleted');
      fetchTestMaps();
    } catch (error) {
      toast.error('Failed to delete test map');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingMap(null);
    setFormData({ name: '', locations: [] });
    stopWalkMode();
  };

  const startWalkMode = () => {
    if (!navigator.geolocation) {
      toast.error('GPS is not available on this device');
      return;
    }
    setWalkMode(true);
    const id = navigator.geolocation.watchPosition(
      (position) => {
        const loc = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setLiveLocation(loc);
        if (mapRef.current) {
          mapRef.current.panTo(loc);
        }
      },
      (err) => {
        toast.error('GPS error: ' + err.message);
      },
      { enableHighAccuracy: true, maximumAge: 2000, timeout: 10000 }
    );
    setGpsWatchId(id);
    toast.success('Walk & Drop active — walk to a spot and tap "Drop Pin"');
  };

  const stopWalkMode = () => {
    if (gpsWatchId !== null) {
      navigator.geolocation.clearWatch(gpsWatchId);
      setGpsWatchId(null);
    }
    setWalkMode(false);
    setLiveLocation(null);
  };

  const dropPinAtCurrentLocation = () => {
    if (!liveLocation) {
      toast.error('Waiting for GPS signal...');
      return;
    }
    const pinNumber = formData.locations.length + 1;
    const newLocation = {
      order: pinNumber,
      name: `Stop ${pinNumber}`,
      lat: liveLocation.lat,
      lng: liveLocation.lng
    };
    setFormData(prev => ({
      ...prev,
      locations: [...prev.locations, newLocation]
    }));
    toast.success(`Pin #${pinNumber} dropped!`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-cyan-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <header className="bg-purple-600 text-white py-6 px-4">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-black uppercase">Test Settings</h1>
              <p className="text-purple-200 mt-1">Set up test locations for tour testing</p>
            </div>
            {!showForm && (
              <button
                onClick={() => setShowForm(true)}
                className="bg-white text-purple-600 px-4 py-2 rounded-full font-bold hover:bg-purple-50 transition-colors flex items-center gap-2"
                data-testid="create-test-map-button"
              >
                <Plus className="w-5 h-5" />
                New Test Map
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Info Box */}
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
          <h3 className="font-bold text-purple-900 mb-2">How Test Mode Works</h3>
          <ul className="text-purple-800 text-sm space-y-1">
            <li>• Create a test map by <strong>clicking on the map</strong> or using <strong>Walk & Drop</strong> to capture your live GPS</li>
            <li>• Each pin is numbered to match the tour stop it replaces (Pin 1 = Stop 1, Pin 2 = Stop 2, etc.)</li>
            <li>• When testing a tour, audio will play at YOUR test locations instead of the actual tour locations</li>
            <li>• If a tour has more stops than test locations, it loops back to the first test location</li>
          </ul>
        </div>

        {/* Form */}
        {showForm && (
          <div className="bg-white rounded-xl border-2 border-purple-200 p-6 mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              {editingMap ? 'Edit Test Map' : 'Create New Test Map'}
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Map Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Home Neighborhood, Office Area"
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200"
                data-testid="test-map-name-input"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Test Locations ({formData.locations.length})
              </label>
              
              {/* Mode Toggle */}
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => { stopWalkMode(); }}
                  data-testid="click-map-mode-button"
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg font-semibold text-sm transition-all ${
                    !walkMode
                      ? 'bg-purple-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <MapPin className="w-4 h-4" />
                  Click on Map
                </button>
                <button
                  type="button"
                  onClick={() => { if (!walkMode) startWalkMode(); }}
                  data-testid="walk-drop-mode-button"
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg font-semibold text-sm transition-all ${
                    walkMode
                      ? 'bg-green-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Navigation className="w-4 h-4" />
                  Walk & Drop
                </button>
              </div>

              {/* Walk & Drop Instructions / Controls */}
              {walkMode && (
                <div className="mb-3">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="font-semibold text-green-800 text-sm">GPS Active</span>
                    </div>
                    <p className="text-green-700 text-xs">
                      Walk to each spot and tap the button below. Pins are numbered to match tour stops.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={dropPinAtCurrentLocation}
                    data-testid="drop-pin-button"
                    className="w-full bg-green-600 hover:bg-green-700 active:scale-95 text-white py-4 rounded-xl font-black text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-3"
                  >
                    <CircleDot className="w-6 h-6" />
                    Drop Pin #{formData.locations.length + 1}
                  </button>
                </div>
              )}

              {!walkMode && (
                <p className="text-sm text-gray-500 mb-2">Click on the map to add test locations. Drag markers to adjust.</p>
              )}
              
              {isLoaded && (
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={mapCenter}
                  zoom={16}
                  onClick={!walkMode ? handleMapClick : undefined}
                  onLoad={(map) => { mapRef.current = map; }}
                  options={{
                    streetViewControl: false,
                    mapTypeControl: false
                  }}
                >
                  {/* Live location blue dot */}
                  {walkMode && liveLocation && (
                    <Marker
                      position={liveLocation}
                      icon={{
                        path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
                        scale: 10,
                        fillColor: '#3B82F6',
                        fillOpacity: 1,
                        strokeColor: '#FFFFFF',
                        strokeWeight: 3,
                      }}
                      zIndex={999}
                    />
                  )}
                  {formData.locations.map((loc, index) => (
                    <Marker
                      key={index}
                      position={{ lat: loc.lat, lng: loc.lng }}
                      label={{ text: String(index + 1), color: 'white', fontWeight: 'bold' }}
                      draggable={!walkMode}
                      onDragEnd={(e) => handleMarkerDrag(index, e)}
                    />
                  ))}
                </GoogleMap>
              )}
            </div>

            {/* Location List */}
            {formData.locations.length > 0 && (
              <div className="mb-4 space-y-2">
                <p className="text-xs text-purple-600 font-semibold uppercase tracking-wide">Pin # = Tour Stop #</p>
                {formData.locations.map((loc, index) => (
                  <div key={index} className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                    <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {index + 1}
                    </div>
                    <input
                      type="text"
                      value={loc.name}
                      onChange={(e) => handleLocationNameChange(index, e.target.value)}
                      placeholder={`Stop ${index + 1} name (optional)`}
                      className="flex-1 px-3 py-1 rounded border border-gray-300 text-sm"
                    />
                    <span className="text-xs text-gray-400 hidden sm:inline">
                      {loc.lat.toFixed(5)}, {loc.lng.toFixed(5)}
                    </span>
                    <button
                      onClick={() => handleRemoveLocation(index)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-2 rounded-lg font-bold transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="save-test-map-button"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Test Map'}
              </button>
              <button
                onClick={handleCancel}
                className="px-6 py-2 rounded-lg border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Test Maps List */}
        {testMaps.length === 0 && !showForm ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Test Maps Yet</h3>
            <p className="text-gray-600 mb-4">Create your first test map to start testing tours in your neighborhood.</p>
            <button
              onClick={() => setShowForm(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-full font-bold transition-colors"
            >
              Create Test Map
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {testMaps.map((testMap) => (
              <div
                key={testMap.map_id}
                className="bg-white rounded-xl border-2 border-gray-200 p-5 hover:border-purple-300 transition-colors"
                data-testid={`test-map-card-${testMap.map_id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <MapPin className="w-6 h-6 text-purple-600" />
                      <h3 className="text-lg font-bold text-gray-900">{testMap.name}</h3>
                    </div>
                    <p className="text-gray-600 text-sm mb-2">
                      {testMap.locations?.length || 0} test location{testMap.locations?.length !== 1 ? 's' : ''}
                    </p>
                    {testMap.locations && testMap.locations.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {testMap.locations.slice(0, 5).map((loc, i) => (
                          <span key={i} className="bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full">
                            {loc.name || `Location ${i + 1}`}
                          </span>
                        ))}
                        {testMap.locations.length > 5 && (
                          <span className="text-gray-400 text-xs">+{testMap.locations.length - 5} more</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleEdit(testMap)}
                      className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all"
                      data-testid="edit-test-map-button"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(testMap.map_id)}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      data-testid="delete-test-map-button"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminTestSettingsPage;
